import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Button, Checkbox, Menu, Card, Tag } from "antd";
import { DownOutlined, UserOutlined, CalendarOutlined, IdcardOutlined } from "@ant-design/icons";

import { AppState } from "store";
import { Table, TablePagination } from "components/base";
import { parseIssuesTable } from "utils/parsers";
import { useHistory } from "react-router-dom";
import {
  setBulkActionsIssues,
  setIssuesSelectedPage,
  setSelectedIssue,
  setSelectedIssuesRows,
} from "store/actions";
import { ActionMenu, statusCell } from "./cell-renders";
import { TableProps } from "antd/lib/table";
import { ROUTES } from "utils/constants";
import "styles/components/tab-content/issues-content/issue-content.scss";

const allColumns: TableProps<any>["columns"] = [
  { title: "Issue ID", dataIndex: "issue_id", width: 130, ellipsis: true },
  { title: "Issue Type", dataIndex: "issue_type", width: 130 },
  { title: "Vehicle/Kiosk", dataIndex: "issue_host", width: 130 },
  { title: "Host ID", dataIndex: "issue_host_id", width: 130, ellipsis: true },
  { title: "Rider Name", dataIndex: "driver_name", ellipsis: true },
  { title: "Created By", dataIndex: "created_by", width: 150, ellipsis: true },
  { title: "Created On", dataIndex: "date_and_time", width: 190, ellipsis: true },
  { title: "Occurred On", dataIndex: "issue_occur_date", width: 160, ellipsis: true },
  { title: "Assignee", dataIndex: "assignee", width: 150, ellipsis: true },
  { title: "Due Date", dataIndex: "due_date", width: 110, ellipsis: true },
  { title: "Status", dataIndex: "status", render: statusCell, width: 100 },
  { title: "Category", dataIndex: "issue_level", width: 100, ellipsis: true },
  {
    title: "",
    dataIndex: "action",
    render: ActionMenu,
    align: "right",
    width: 56,
    className: "action-menu open-link --issues",
  },
];

// Mobile Card Component
const MobileIssueCard = ({ issue, onClick }: any) => {
  return (
    <Card
      className="mobile-issue-card"
      onClick={() => onClick(issue.raw)}
      hoverable
    >
      <div className="card-header">
        <div className="card-title">
          <span className="issue-id">{issue.issue_id}</span>
        </div>
        <div className="card-status">{statusCell(issue.status)}</div>
      </div>

      <div className="card-body">
        <div className="card-row">
          <span className="label">Type:</span>
          <span className="value">{issue.issue_type}</span>
        </div>

        <div className="card-row">
          <span className="label">Host:</span>
          <span className="value">{issue.issue_host}</span>
        </div>

        {/* {issue.driver_name && (
          <div className="card-row">
            <UserOutlined className="row-icon" />
            <span className="value">{issue.driver_name}</span>
          </div>
        )} */}

        {issue.assignee && (
          <div className="card-row">
            <span className="label">Assignee:</span>
            <span className="value">{issue.assignee}</span>
          </div>
        )}

        <div className="card-row">
          <span className="label">Created On:</span>
          <span className="value">{issue.date_and_time}</span>
        </div>

        {issue.due_date && (
          <div className="card-row">
            <span className="label">Due:</span>
            <span className="value due-date">{issue.due_date}</span>
          </div>
        )}

        {issue.issue_level && (
          <div className="card-footer">
            <Tag color="blue">{issue.issue_level}</Tag>
          </div>
        )}
      </div>

      <div className="card-action">
        <ActionMenu {...issue} />
      </div>
    </Card>
  );
};

export default function TableView() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 992
  );

  const {
    issues,
    page,
    search,
    selectedIssuesRows,
    filters: { listType },
  } = useSelector<AppState, AppState["issues"]>((state) => state.issues);
  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const { selectedPage, totalPages } = page;
  const data = useMemo(
    () => parseIssuesTable(search, issues, listType),
    [issues, listType, search]
  );

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Column visibility state
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    const saved = localStorage.getItem("issueTableColumns");
    if (saved) return JSON.parse(saved);
    return (allColumns ?? [])
      .filter((c) => "dataIndex" in c && c.dataIndex !== "action")
      .map((c) => (c as { dataIndex: string }).dataIndex);
  });

  useEffect(() => {
    localStorage.setItem("issueTableColumns", JSON.stringify(visibleKeys));
  }, [visibleKeys]);

  const handleRowClick = (issue: any) => {
    dispatch(setSelectedIssue(issue));
    history.push(`${ROUTES.issue_detail}/${issue?.id}`);
  };

  // Responsive column filtering
  const getResponsiveColumns = useCallback(() => {
    let columnsToShow = [...visibleKeys];

    // Tablet view - show essential columns only
    if (isTablet) {
      columnsToShow = visibleKeys.filter((key) =>
        [
          "issue_id",
          "issue_type",
          "issue_host",
          "assignee",
          "status",
          "due_date",
        ].includes(key)
      );
    }

    // Mobile view handled separately with cards
    if (isMobile) {
      return [];
    }

    return (allColumns ?? []).filter(
      (c) =>
        "dataIndex" in c &&
        (c.dataIndex === "action" || columnsToShow.includes(c.dataIndex as string))
    );
  }, [visibleKeys, isMobile, isTablet]);

  const filteredColumns = useMemo(() => getResponsiveColumns(), [getResponsiveColumns]);

  // Row selection
  const onChangeSelection = useCallback(
    (value) => {
      dispatch(setSelectedIssuesRows(value));
      dispatch(setBulkActionsIssues(value.length !== 0));
    },
    [dispatch]
  );

  const onBack = useCallback(() => {
    if (selectedPage > 1) {
      dispatch(setIssuesSelectedPage(selectedPage - 1));
    }
  }, [dispatch, selectedPage]);

  const onNext = useCallback(() => {
    if (selectedPage < totalPages) {
      dispatch(setIssuesSelectedPage(selectedPage + 1));
    }
  }, [dispatch, selectedPage, totalPages]);

  const resetCallback = useCallback(() => {
    dispatch(setIssuesSelectedPage(1));
  }, [dispatch]);

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const columnMenu =
    userType === 2 ? (
      <Menu selectable={false} className="column-selector-menu">
        {(allColumns ?? [])
          .filter((col) =>
            "dataIndex" in col ? col.dataIndex !== "action" : true
          )
          .map((col) => {
            const key =
              "children" in col ? col.key : (col as any).dataIndex || col.key;
            return (
              <Menu.Item
                key={key}
                onClick={(e) => e.domEvent.stopPropagation()}
              >
                <Checkbox
                  checked={visibleKeys.includes(key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setVisibleKeys([...visibleKeys, key]);
                    } else {
                      setVisibleKeys(visibleKeys.filter((k) => k !== key));
                    }
                  }}
                >
                  {col.title || "Column"}
                </Checkbox>
              </Menu.Item>
            );
          })}
      </Menu>
    ) : null;

  return (
    <div className="issues-table-container">
      {userType === 2 && columnMenu && !isMobile && (
        <div className="table-controls">
          <Dropdown
            overlay={columnMenu}
            trigger={["click"]}
            visible={dropdownOpen}
            onVisibleChange={(visible: boolean) => setDropdownOpen(visible)}
          >
            <Button className="column-selector-btn">
              Select Columns <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      )}

      {isMobile ? (
        <div className="mobile-issues-list">
          {data.map((issue, index) => (
            <MobileIssueCard
              key={issue.key || index}
              issue={issue}
              onClick={handleRowClick}
            />
          ))}
        </div>
      ) : (
        <div className="desktop-table-wrapper">
          <Table
            columns={filteredColumns}
            dataSource={data}
            onRow={(record) => ({
              onClick: () => handleRowClick(record.raw),
            })}
            pagination={false}
            // scroll={{ 
            //   y: isMobile ? "calc(100vh - 250px)" : "70vh", 
            //   x: isTablet ? 800 : "100%" 
            // }}
            className="issues-table-responsive"
          />
        </div>
      )}
 <div className="pagination-wrapper">
      <TablePagination
        page={page}
        onBack={onBack}
        onNext={onNext}
        resetCallback={resetCallback}
      />
      </div>
    </div>
  );
}